import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowUpRight, Mail } from 'lucide-react';

import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Field from '@/components/forms/Field';
import Checkbox from '@/components/forms/Checkbox';
import CodeInput from '@/components/forms/CodeInput';

import { signupStart, signupVerify, signupResend } from '@/lib/publicApi';
import { createBillingCheckout } from '@/lib/billingApi';
import { saveSession, isLoggedIn } from '@/lib/auth';
import { ApiError } from '@/lib/api';
import { useSearchParams } from 'react-router-dom';

type Step = 'form' | 'code';

const REF_STORAGE_KEY = 'resstro_signup_ref';

// Простейшая маска для телефона. Достаточно для отображения;
// нормализация финальная, на сервере (phone.ts).
function formatPhoneMask(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 10);
  const d = digits;
  if (!d) return '';
  const a = d.slice(0, 3);
  const b = d.slice(3, 6);
  const c = d.slice(6, 8);
  const e = d.slice(8, 10);
  let out = a;
  if (b) out += ' ' + b;
  if (c) out += '-' + c;
  if (e) out += '-' + e;
  return out;
}

function useCountdown(from: string | null): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!from) return 0;
  const diff = Math.max(0, Math.ceil((new Date(from).getTime() - now) / 1000));
  return diff;
}

export default function Signup() {
  const navigate = useNavigate();

  // Уже залогиненному регистрироваться повторно нет смысла, отправляем
  // его в кабинет. Иначе он бы натыкался на «email уже занят» от бэка
  // и думал что что-то сломалось.
  useEffect(() => {
    if (isLoggedIn()) navigate('/cabinet', { replace: true });
  }, [navigate]);

  const [step, setStep] = useState<Step>('form');

  // Шаг 1, форма
  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [address, setAddress] = useState('');
  const [email, setEmail] = useState('');
  const [phoneRaw, setPhoneRaw] = useState('');
  const [agree, setAgree] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Шаг 2, код
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [nextResendAt, setNextResendAt] = useState<string | null>(null);
  const resendSeconds = useCountdown(nextResendAt);

  const phone = useMemo(() => phoneRaw.replace(/\D/g, ''), [phoneRaw]);

  const topRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [step]);

  const validateForm = (): boolean => {
    const e: Record<string, string> = {};
    if (name.trim().length < 2) e.name = 'Минимум 2 символа';
    if (city.trim().length < 2) e.city = 'Укажите город';
    if (address.trim().length < 5) e.address = 'Укажите адрес: улица, дом';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) e.email = 'Введите корректный email';
    if (phone.length !== 10) e.phone = 'Введите 10 цифр номера';
    if (!agree) e.agree = 'Необходимо согласие';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (evt: React.FormEvent) => {
    evt.preventDefault();
    setFormError(null);
    if (!validateForm()) return;
    setSubmitting(true);
    try {
      const res = await signupStart({
        name: name.trim(),
        city: city.trim(),
        address: address.trim(),
        email: email.trim().toLowerCase(),
        phone: '+7' + phone,
        agree: true,
        marketing,
        ref: refCode,
      });
      setNextResendAt(res.nextAt);
      setCode('');
      setCodeError(null);
      setStep('code');
    } catch (err) {
      if (err instanceof ApiError) setFormError(err.message);
      else setFormError('Не удалось отправить заявку. Попробуйте позже.');
    } finally {
      setSubmitting(false);
    }
  };

  const [searchParams] = useSearchParams();
  const wantsPlan = searchParams.get('plan'); // single | start | business | network

  useEffect(() => {
    const ref = searchParams.get('ref')?.trim();
    if (ref) localStorage.setItem(REF_STORAGE_KEY, ref);
  }, [searchParams]);

  const refCode = useMemo(() => {
    const fromUrl = searchParams.get('ref')?.trim();
    if (fromUrl) return fromUrl;
    return localStorage.getItem(REF_STORAGE_KEY)?.trim() || undefined;
  }, [searchParams]);
  const wantsBilling = searchParams.get('billing') === 'year' ? 'YEARLY' : 'MONTHLY';

  const handleVerify = async (codeToSend?: string) => {
    const c = (codeToSend ?? code).trim();
    if (c.length < 4) return;
    setVerifying(true);
    setCodeError(null);
    try {
      const session = await signupVerify({ email: email.trim().toLowerCase(), code: c });
      saveSession(session);

      // Если пользователь пришёл с лендинга по «Выбрать тариф», после успешной
      // регистрации сразу запускаем оплату, не гоняем его по двум переходам.
      // Оплачивается первое (созданное при регистрации) заведение; остальные
      // точки владелец добавит в админке — у каждой своя подписка и оплата.
      if (wantsPlan) {
        try {
          const co = await createBillingCheckout({
            planCode: wantsPlan,
            billingPeriod: wantsBilling,
          });
          window.location.href = co.confirmationUrl;
          return;
        } catch (err) {
          // Если checkout упал, просто идём в кабинет и покажем модалку оплаты руками.
          // eslint-disable-next-line no-console
          console.error('post-signup checkout failed', err);
        }
      }

      navigate('/cabinet?welcome=1', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) setCodeError(err.message);
      else setCodeError('Не удалось подтвердить. Попробуйте ещё раз.');
    } finally {
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    if (resendSeconds > 0) return;
    setResending(true);
    setCodeError(null);
    try {
      const res = await signupResend({ email: email.trim().toLowerCase() });
      setNextResendAt(res.nextAt);
      setCode('');
    } catch (err) {
      if (err instanceof ApiError) setCodeError(err.message);
      else setCodeError('Не удалось отправить повторно.');
    } finally {
      setResending(false);
    }
  };

  return (
    <>
      <Header />
      <main className="section-divider bg-paper">
        <div className="container-site py-16 sm:py-24" ref={topRef}>
          <div className="mx-auto grid max-w-5xl gap-10 md:grid-cols-12">
            {/* Левая колонка, заголовок и преимущества */}
            <aside className="md:col-span-5">
              <span className="eyebrow">Регистрация</span>
              <h1 className="h2 mt-5">
                7 дней
                <br />
                <span className="text-ink/40">без ограничений</span>
              </h1>
              <p className="lead mt-6 max-w-sm">
                Создайте аккаунт и получите пробный доступ ко всем возможностям Resstro:
                админ-панели, кассе и электронному меню.
              </p>
              <ul className="mt-10 space-y-4 text-[14px] text-ink/80">
                <li className="flex gap-3">
                  <span className="mt-[9px] h-1 w-1 shrink-0 rounded-full bg-orange" aria-hidden />
                  <span>Без привязки карты</span>
                </li>
                <li className="flex gap-3">
                  <span className="mt-[9px] h-1 w-1 shrink-0 rounded-full bg-orange" aria-hidden />
                  <span>Отмена в один клик, без штрафов</span>
                </li>
                <li className="flex gap-3">
                  <span className="mt-[9px] h-1 w-1 shrink-0 rounded-full bg-orange" aria-hidden />
                  <span>Поддержка на русском в WhatsApp</span>
                </li>
              </ul>
              <p className="mt-10 text-xs text-ink/50">
                Уже зарегистрированы?{' '}
                <Link to="/login" className="link">
                  Войти в личный кабинет
                </Link>
              </p>
            </aside>

            {/* Правая колонка, форма */}
            <section className="md:col-span-7">
              <div className="border border-line bg-white p-6 sm:p-10">
                {step === 'form' ? (
                  <form onSubmit={handleSubmit} noValidate>
                    <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-ink/50">
                      Шаг 1 · Данные аккаунта
                    </div>
                    <h2 className="mt-3 text-[22px] font-medium leading-tight tracking-tightest text-ink">
                      Зарегистрируйтесь в Resstro
                    </h2>

                    <div className="mt-8 space-y-5">
                      <Field
                        label="Название вашего заведения"
                        name="name"
                        autoComplete="organization"
                        placeholder="Например, кафе «Ромашка»"
                        value={name}
                        onChange={(e) => {
                          setName(e.target.value);
                          if (errors.name) setErrors({ ...errors, name: '' });
                        }}
                        error={errors.name || null}
                      />
                      <div className="grid gap-5 sm:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
                        <Field
                          label="Город"
                          name="city"
                          autoComplete="address-level2"
                          placeholder="Махачкала"
                          value={city}
                          onChange={(e) => {
                            setCity(e.target.value);
                            if (errors.city) setErrors({ ...errors, city: '' });
                          }}
                          error={errors.city || null}
                        />
                        <Field
                          label="Адрес заведения"
                          name="address"
                          autoComplete="street-address"
                          placeholder="ул. Ленина, 10"
                          value={address}
                          onChange={(e) => {
                            setAddress(e.target.value);
                            if (errors.address) setErrors({ ...errors, address: '' });
                          }}
                          error={errors.address || null}
                        />
                      </div>
                      <Field
                        label="Email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        inputMode="email"
                        placeholder="you@company.ru"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          if (errors.email) setErrors({ ...errors, email: '' });
                        }}
                        error={errors.email || null}
                      />
                      <Field
                        label="Телефон"
                        name="phone"
                        type="tel"
                        autoComplete="tel"
                        inputMode="numeric"
                        placeholder="912 345-67-89"
                        value={formatPhoneMask(phoneRaw)}
                        onChange={(e) => {
                          setPhoneRaw(e.target.value);
                          if (errors.phone) setErrors({ ...errors, phone: '' });
                        }}
                        prefix={<span className="font-medium text-ink">+7</span>}
                        error={errors.phone || null}
                      />
                    </div>

                    <div className="mt-8 space-y-3">
                      <Checkbox
                        checked={agree}
                        onChange={(v) => {
                          setAgree(v);
                          if (errors.agree) setErrors({ ...errors, agree: '' });
                        }}
                        error={errors.agree || null}
                      >
                        Я ознакомлен(а) с{' '}
                        <Link to="/legal/privacy" className="link">
                          политикой
                        </Link>
                        ,{' '}
                        <Link to="/legal/offer" className="link">
                          офертой
                        </Link>{' '}
                        и даю{' '}
                        <Link to="/legal/data" className="link">
                          согласие
                        </Link>{' '}
                        на обработку персональных данных
                      </Checkbox>
                      <Checkbox checked={marketing} onChange={setMarketing}>
                        Я даю согласие на получение информационных рассылок
                      </Checkbox>
                    </div>

                    {errors.agree && (
                      <p className="mt-3 text-[12px] text-orange">{errors.agree}</p>
                    )}
                    {formError && (
                      <div className="mt-4 border border-orange bg-orange/5 px-4 py-3 text-[13px] text-ink">
                        {formError}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={submitting || !agree}
                      className="mt-8 btn-primary w-full justify-between disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <span>{submitting ? 'Отправляем код…' : 'Зарегистрироваться'}</span>
                      <ArrowUpRight size={16} strokeWidth={1.75} />
                    </button>

                    <p className="mt-4 text-center text-[11px] leading-relaxed text-ink/50">
                      Пробный период 7 дней · Без привязки карты
                    </p>
                  </form>
                ) : (
                  <div>
                    <button
                      type="button"
                      onClick={() => setStep('form')}
                      className="inline-flex items-center gap-2 text-sm text-ink/60 hover:text-ink transition"
                    >
                      <ArrowLeft size={16} strokeWidth={1.6} />
                      Изменить данные
                    </button>

                    <div className="mt-6 text-[11px] font-medium uppercase tracking-[0.14em] text-ink/50">
                      Шаг 2 · Подтверждение
                    </div>
                    <h2 className="mt-3 text-[22px] font-medium leading-tight tracking-tightest text-ink">
                      Введите код из письма
                    </h2>
                    <p className="mt-3 flex items-center gap-2 text-sm text-ink/60">
                      <Mail size={14} strokeWidth={1.6} /> Отправили на {email.toLowerCase()}
                    </p>

                    <div className="mt-8">
                      <CodeInput
                        value={code}
                        onChange={(v) => {
                          setCode(v);
                          if (codeError) setCodeError(null);
                        }}
                        onComplete={(v) => handleVerify(v)}
                        autoFocus
                        disabled={verifying}
                        error={!!codeError}
                      />
                      {codeError && (
                        <p className="mt-3 text-[13px] text-orange">{codeError}</p>
                      )}
                    </div>

                    <button
                      type="button"
                      onClick={() => handleVerify()}
                      disabled={verifying || code.length < 4}
                      className="mt-8 btn-primary w-full justify-between disabled:opacity-60"
                    >
                      <span>{verifying ? 'Проверяем…' : 'Подтвердить и войти'}</span>
                      <ArrowUpRight size={16} strokeWidth={1.75} />
                    </button>

                    <div className="mt-6 flex items-center justify-between text-[13px] text-ink/60">
                      <span>Не пришёл код?</span>
                      <button
                        type="button"
                        onClick={handleResend}
                        disabled={resendSeconds > 0 || resending}
                        className="link disabled:no-underline disabled:opacity-50"
                      >
                        {resendSeconds > 0
                          ? `Отправить повторно через ${resendSeconds} с`
                          : resending
                            ? 'Отправляем…'
                            : 'Отправить повторно'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
